import { createAction as ca, createReducer } from '@reduxjs/toolkit';
import _ from 'lodash';
import {
  CpWidgetVariantType,
  CustomerPortal,
  CustomerPortalBlock,
  CustomerPortalPage,
  CustomerPortalPageType,
  CustomerPortalSection,
  CustomerPortalWidgetType,
} from 'api/customer-portal/types';
import { reorder } from 'utils/reorder';
import { floatUidString } from 'utils/uid';
import { QueryCombinerType } from '../tasks/combiner';

export const getUndefinedNumber = () => undefined as unknown as number;

export const DEFAULT_CP_STATE: CustomerPortal = (() => {
  const homePageReferenceId = floatUidString();
  const chatPageReferenceId = floatUidString();

  return {
    customerPortalThemeId: getUndefinedNumber(),
    customerPortalId: getUndefinedNumber(),
    customerPortalName: 'Untitled',
    portalMeta: {
      pagesOrder: [],
    },
    pages: [
      {
        pageType: CustomerPortalPageType.Home,
        customerPortalPageId: getUndefinedNumber(),
        pageMeta: {
          sectionsOrder: [],
          conditions: {
            match: QueryCombinerType.Any,
            customFields: [],
            nativeFields: [],
          },
        },
        // TODO: add translations
        pageName: 'Home page',
        pageReferenceId: homePageReferenceId,
        parentPortalId: getUndefinedNumber(),
        routePath: '/',
        sections: [],
        deleteable: false,
      },
      {
        pageType: CustomerPortalPageType.Chat,
        customerPortalPageId: getUndefinedNumber(),
        pageMeta: {
          sectionsOrder: [],
          conditions: {
            match: QueryCombinerType.Any,
            customFields: [],
            nativeFields: [],
          },
        },
        // TODO: add translations
        pageName: 'Chat page',
        pageReferenceId: chatPageReferenceId,
        parentPortalId: getUndefinedNumber(),
        routePath: '/chats',
        sections: [
          {
            blocks: [
              {
                blockDefinition: {
                  blockType: 'Chat',
                  blockName: 'Chat',
                  blockReferenceId: floatUidString(),
                  deleted: false,
                },
                widget: {
                  widgetType: CustomerPortalWidgetType.CHATS,
                  widgetVariantType: CpWidgetVariantType.Default,
                  widgetReferenceId: floatUidString(),
                  deleted: false,
                  widgetDefinition: {
                    input: {},
                    styles: {},
                    conditions: {
                      match: QueryCombinerType.Any,
                      customFields: [],
                      nativeFields: [],
                    },
                  },
                },
                blockId: floatUidString(),
                blockName: 'Chat',
              },
            ],
            customerPortalSectionId: getUndefinedNumber(),
            parentPageId: getUndefinedNumber(),
            parentPortalId: getUndefinedNumber(),
            sectionMeta: {
              conditions: {
                match: QueryCombinerType.Any,
                customFields: [],
                nativeFields: [],
              },
            },
            sectionName: 'Chat',
            sectionReferenceId: floatUidString(),
            deleted: false,
          },
        ],
        deleteable: false,
      },
      // Account Overview Page
      {
        customerPortalPageId: getUndefinedNumber(),
        pageType: CustomerPortalPageType.AccountOverview,
        pageMeta: {
          conditions: {
            match: QueryCombinerType.Any,
            customFields: [],
            nativeFields: [],
          },
        },
        pageName: 'Account Overview',
        pageReferenceId: floatUidString(),
        parentPortalId: getUndefinedNumber(),
        routePath: '/overview',
        sections: [],
        deleteable: false,
      },
    ],
  } as CustomerPortal;
})();

export const NOTIFICATIONS_PAGE = {
  customerPortalPageId: getUndefinedNumber(),
  pageType: CustomerPortalPageType.Custom,
  pageMeta: {
    conditions: {
      match: QueryCombinerType.Any,
      customFields: [],
      nativeFields: [],
    },
  },
  // TODO: add translations
  pageName: 'Notifications page',
  pageReferenceId: floatUidString(),
  parentPortalId: getUndefinedNumber(),
  routePath: '/notifications',
  sections: [],
  deleteable: false,
};

export const DEFAULT_CP_PAGE: CustomerPortalPage = (() => {
  const pageReferenceId = floatUidString();

  return {
    customerPortalPageId: getUndefinedNumber(),
    pageType: CustomerPortalPageType.Home,

    pageMeta: {
      sectionsOrder: [],
      conditions: {
        match: QueryCombinerType.Any,
        customFields: [],
        nativeFields: [],
      },
    },
    // TODO: add translations
    pageName: '',
    pageReferenceId,
    parentPortalId: getUndefinedNumber(),
    routePath: '/',
    sections: [],
  };
})();

const createAction = <T>(a: string) =>
  ca<T & { handleSuccess?: (arg?: unknown) => void }>(a);

type UpdatePagePayload = Omit<
  CustomerPortalPage,
  'customerPortalPageId' | 'pageReferenceId' | 'pageType'
>;

type UpdateSectionPayload = Omit<
  CustomerPortalSection,
  'sectionReferenceId' | 'customerPortalSectionId'
>;

type CreateBlockPayload = Omit<CustomerPortalBlock, 'blockId' | 'blockName'>;

export const renamePortal = createAction<{ newName: string }>(
  'customerPortal/renamePortal',
);

export const addPage = createAction<UpdatePagePayload>(
  'customerPortal/addPage',
);

export const addPageNextTo = createAction<{
  nextTo: string;
  page: UpdatePagePayload;
}>('customerPortal/addPageNextTo');

export const addSectionToPage = createAction<{
  pageReferenceId: string;
  section: UpdateSectionPayload;
}>('cp/addSectionToPage');

export const addSectionBelowToPage = createAction<{
  pageReferenceId: string;
  section: UpdateSectionPayload;
  aboveSectionReferenceId: string;
}>('cp/addSectionBelowToPage');

// above
export const addSectionAboveToPage = createAction<{
  pageReferenceId: string;
  section: UpdateSectionPayload;
  belowSectionReferenceId: string;
}>('cp/addSectionAboveToPage');

export const deletePage =
  createAction<{ pageReferenceId: string }>('cp/deletePage');

export const deleteSection =
  createAction<{ sectionReferenceId: string; pageReferenceId: string }>(
    'cp/deleteSection',
  );

export const updatePage =
  createAction<{ pageReferenceId: string; payload: UpdatePagePayload }>(
    'cp/updatePage',
  );

export const updatePageName =
  createAction<{ pageReferenceId: string; pageName: string }>(
    'cp/updatePageName',
  );

export const updateSection =
  createAction<{
    sectionReferenceId: string;
    pageReferenceId: string;
    section: UpdateSectionPayload;
  }>('cp/updateSection');

export const updateSectionName = createAction<{
  sectionReferenceId: string;
  pageReferenceId: string;
  sectionName: string;
}>('cp/updateSectionName');

export const addBlockToSection = createAction<{
  pageReferenceId: string;
  sectionReferenceId: string;
  blockPayload: CreateBlockPayload;
}>('cp/addBlockToSection');

export const deleteBlockInSection = createAction<{
  pageReferenceId: string;
  sectionReferenceId: string;
  blockId: string;
}>('cp/deleteBlockInSection');

export const updateBlockInSection = createAction<{
  pageReferenceId: string;
  sectionReferenceId: string;
  blockId: string;
  blockPayload: CreateBlockPayload;
}>('cp/updateBlockInSection');

export const initialiseCustomerPortal =
  createAction<{ cp: CustomerPortal }>('cp/initialise');

export const reorderPages = createAction<{
  source: number;
  destination: number;
}>('customerPortal/reorderPages');

export const reorderSections = createAction<{
  source: number;
  destination: number;
  pageReferenceId: string;
}>('customerPortal/reorderSections');

export const changeTheme = createAction<{ themeId: number }>(
  'customerPortal/changeTheme',
);

export const copyToPage =
  createAction<{
    pageReferenceId: string;
    sectionReferenceId: string;
  }>('cp/copyToPage');

export const moveToPage =
  createAction<{
    pageReferenceId: string;
    sectionReferenceId: string;
  }>('cp/moveToPage');

export const duplicateToPage =
  createAction<{
    pageReferenceId: string;
    sectionReferenceId: string;
    handleSuccess?: (arg?: string) => void;
  }>('cp/duplicateToPage');

export const customerPortalReducer = createReducer(
  DEFAULT_CP_STATE,
  (builder) => {
    builder.addCase(renamePortal, (state, action) => {
      if (action.payload.newName) {
        state.customerPortalName = action.payload.newName;
      }
    });

    builder.addCase(initialiseCustomerPortal, (state, action) => {
      const { payload } = action;
      const portal = payload?.cp;

      state.createdAt = portal.createdAt;
      state.createdBy = portal.createdBy;
      state.updatedAt = portal.updatedAt;
      state.updatedBy = portal.updatedBy;
      state.customerPortalId = portal.customerPortalId;
      state.customerPortalName = portal.customerPortalName;
      state.pages = portal.pages;
      action.payload.handleSuccess?.();
    });

    builder.addCase(addPage, (state, action) => {
      const newPage = action.payload;

      const { pages } = state;

      pages.push({
        pageMeta: newPage.pageMeta,
        pageName: newPage.pageName,
        pageReferenceId: floatUidString(),
        routePath: newPage.routePath,
        sections: [],
        deleted: false,
        pageType: CustomerPortalPageType.Custom,
        parentPortalId: getUndefinedNumber(),
        customerPortalPageId: getUndefinedNumber(),
      });
      action.payload.handleSuccess?.();
    });

    builder.addCase(addPageNextTo, (state, action) => {
      const { nextTo, page: newPage } = action.payload;

      const { pages, portalMeta } = state;

      const nextToPage = pages?.find((e) => e?.pageReferenceId === nextTo);

      const pageReferenceId = floatUidString();

      const newPageToBePushed: CustomerPortalPage = {
        pageMeta: newPage.pageMeta,
        pageName: newPage.pageName,
        pageReferenceId,
        pageType: CustomerPortalPageType.Custom,
        routePath: newPage.routePath,
        sections: [],
        deleted: false,
        parentPortalId: getUndefinedNumber(),
        customerPortalPageId: getUndefinedNumber(),
      };

      pages.push(newPageToBePushed);
      if (nextToPage) {
        const index = portalMeta?.pagesOrder?.indexOf(nextTo);
        const insertAt = index + 1;

        if (state?.portalMeta?.pagesOrder) {
          state.portalMeta.pagesOrder = [
            ...state.portalMeta.pagesOrder.slice(0, insertAt),
            pageReferenceId,
            ...state.portalMeta.pagesOrder.slice(insertAt),
          ];
        }
      }
      action.payload.handleSuccess?.(newPageToBePushed);
    });

    builder.addCase(addSectionToPage, (state, action) => {
      const { section, pageReferenceId } = action.payload;

      const { pages } = state;

      const page = pages.find((e) => e.pageReferenceId === pageReferenceId);

      if (page) {
        const sectionReferenceId = floatUidString();
        const newSection: CustomerPortalSection = {
          blocks: section?.blocks ?? [],
          customerPortalSectionId: getUndefinedNumber(),
          parentPageId: page?.customerPortalPageId,
          parentPortalId: state?.customerPortalId,
          sectionMeta: section.sectionMeta,
          sectionName: section.sectionName,
          sectionReferenceId,
          deleted: false,
        };

        if (!page.sections) {
          page.sections = [newSection];
        } else {
          page.sections.push(newSection);
        }
        if (page?.pageMeta?.sectionsOrder) {
          page.pageMeta.sectionsOrder.push(sectionReferenceId);
        } else {
          page.pageMeta = {
            ...(page?.pageMeta ?? {}),
            sectionsOrder: [sectionReferenceId],
          };
        }

        action.payload.handleSuccess?.(newSection);
      }
    });

    builder.addCase(addSectionBelowToPage, (state, action) => {
      const { section, pageReferenceId, aboveSectionReferenceId } =
        action.payload;

      const { pages } = state;

      const page = pages.find((e) => e.pageReferenceId === pageReferenceId);

      if (page) {
        const sectionReferenceId = floatUidString();
        const aboveSection = page.sections.find(
          (e) => e.sectionReferenceId === aboveSectionReferenceId,
        );

        if (aboveSection) {
          const aboveIndex = page.pageMeta?.sectionsOrder.indexOf(
            aboveSection?.sectionReferenceId,
          );
          const insertAt = aboveIndex + 1;

          const newSection: CustomerPortalSection = {
            blocks: section?.blocks ?? [],
            customerPortalSectionId: getUndefinedNumber(),
            parentPageId: page?.customerPortalPageId,
            parentPortalId: state?.customerPortalId,
            sectionMeta: section.sectionMeta,
            sectionName: section.sectionName,
            sectionReferenceId,
            deleted: false,
          };

          if (!page.sections) {
            page.sections = [newSection];
          } else {
            // add section below the above section

            page.sections = [
              ...page.sections.slice(0, insertAt),
              newSection,
              ...page.sections.slice(insertAt),
            ];
          }
          if (page?.pageMeta?.sectionsOrder) {
            page.pageMeta.sectionsOrder = [
              ...page.pageMeta.sectionsOrder.slice(0, insertAt),
              newSection?.sectionReferenceId,
              ...page.pageMeta.sectionsOrder.slice(insertAt),
            ];
          } else {
            page.pageMeta = {
              ...(page?.pageMeta ?? {}),
              sectionsOrder: [sectionReferenceId],
            };
          }

          action.payload.handleSuccess?.(newSection);
        }
      }
    });

    // above

    builder.addCase(addSectionAboveToPage, (state, action) => {
      const { section, pageReferenceId, belowSectionReferenceId } =
        action.payload;

      const { pages } = state;

      const page = pages.find((e) => e.pageReferenceId === pageReferenceId);

      if (page) {
        const sectionReferenceId = floatUidString();
        const belowSection = page.sections.find(
          (e) => e.sectionReferenceId === belowSectionReferenceId,
        );

        if (belowSection) {
          const belowIndex = page.pageMeta?.sectionsOrder.indexOf(
            belowSection?.sectionReferenceId,
          );
          const insertAt = belowIndex;

          const newSection: CustomerPortalSection = {
            blocks: section?.blocks ?? [],
            customerPortalSectionId: getUndefinedNumber(),
            parentPageId: page?.customerPortalPageId,
            parentPortalId: state?.customerPortalId,
            sectionMeta: section.sectionMeta,
            sectionName: section.sectionName,
            sectionReferenceId,
            deleted: false,
          };

          if (!page.sections) {
            page.sections = [newSection];
          } else {
            // add section above the below section
            page.sections = [
              ...page.sections.slice(0, insertAt),
              newSection,
              ...page.sections.slice(insertAt),
            ];
          }
          if (page?.pageMeta?.sectionsOrder) {
            page.pageMeta.sectionsOrder = [
              ...page.pageMeta.sectionsOrder.slice(0, insertAt),
              sectionReferenceId,
              ...page.pageMeta.sectionsOrder.slice(insertAt),
            ];
          } else {
            page.pageMeta = {
              ...(page?.pageMeta ?? {}),
              sectionsOrder: [sectionReferenceId],
            };
          }

          action.payload.handleSuccess?.(newSection);
        }
      }
    });

    builder.addCase(addBlockToSection, (state, action) => {
      const { sectionReferenceId, pageReferenceId, blockPayload } =
        action.payload;

      const { pages } = state;

      const page = pages.find((e) => e.pageReferenceId === pageReferenceId);

      if (page) {
        const { sections } = page;

        if (sections) {
          const section = sections.find(
            (e) => e.sectionReferenceId === sectionReferenceId,
          );

          if (section) {
            const { blocks } = section;
            const newBlock: CustomerPortalBlock = {
              blockDefinition: blockPayload.blockDefinition,
              widget: blockPayload.widget,
              blockId: floatUidString(),
            };

            if (blocks) {
              blocks.push(newBlock);
            } else {
              section.blocks = [newBlock];
            }
          }
        }
      }
      action.payload.handleSuccess?.();
    });

    builder.addCase(deletePage, (state, action) => {
      const { pageReferenceId } = action.payload;

      const { pages, portalMeta } = state;

      const index = pages.findIndex(
        (e) => e.pageReferenceId === pageReferenceId,
      );

      if (index > -1) {
        const page = pages[index];

        pages[index] = _.cloneDeep({ ...page, deleted: true });
        if (portalMeta?.pagesOrder) {
          portalMeta.pagesOrder = portalMeta.pagesOrder.filter(
            (e) => e !== pageReferenceId,
          );
        }
      }
      action.payload.handleSuccess?.();
    });

    builder.addCase(deleteSection, (state, action) => {
      const { pageReferenceId, sectionReferenceId } = action.payload;

      const { pages } = state;

      const page = pages.find((e) => e.pageReferenceId === pageReferenceId);

      if (page) {
        const { sections, pageMeta } = page;

        if (sections) {
          const index = sections.findIndex(
            (e) => e.sectionReferenceId === sectionReferenceId,
          );
          const section = sections[index];

          sections[index] = _.cloneDeep({ ...section, deleted: true });
          if (pageMeta?.sectionsOrder) {
            pageMeta.sectionsOrder = pageMeta?.sectionsOrder.filter(
              (e) => e !== sectionReferenceId,
            );
          }
        }
      }
      action.payload.handleSuccess?.();
    });

    builder.addCase(deleteBlockInSection, (state, action) => {
      const { pageReferenceId, sectionReferenceId, blockId } = action.payload;

      const { pages } = state;

      const page = pages.find((e) => e.pageReferenceId === pageReferenceId);

      if (page) {
        const { sections } = page;

        if (sections) {
          const section = sections.find(
            (e) => e.sectionReferenceId === sectionReferenceId,
          );

          if (section) {
            const { blocks } = section;

            if (blocks) {
              section.blocks = blocks.filter((e) => e.blockId !== blockId);
            }
          }
        }
      }
      action.payload.handleSuccess?.();
    });

    builder.addCase(updatePage, (state, action) => {
      const { payload: newPage, pageReferenceId } = action.payload;

      const { pages } = state;

      if (pages) {
        const page = pages.find((e) => e.pageReferenceId === pageReferenceId);

        if (page) {
          const update: CustomerPortalPage = {
            pageType: page.pageType,
            pageMeta: {
              sectionsOrder:
                newPage?.pageMeta?.sectionsOrder ??
                page?.pageMeta?.sectionsOrder,
              conditions:
                newPage?.pageMeta?.conditions ?? page?.pageMeta?.conditions,
            },
            pageName: newPage.pageName,
            routePath: newPage.routePath,
            sections: page.sections,
            deleted: page.deleted,
            pageReferenceId: page.pageReferenceId,
            parentPortalId: page.parentPortalId,
            customerPortalPageId: page.customerPortalPageId,
          };

          const index = pages.indexOf(page);

          pages[index] = update;
        }
      }
      action.payload.handleSuccess?.();
    });

    builder.addCase(updatePageName, (state, action) => {
      const { pageName, pageReferenceId } = action.payload;

      const { pages } = state;

      if (pages) {
        const page = pages.find((e) => e.pageReferenceId === pageReferenceId);

        if (page) {
          page.pageName = pageName;
        }
      }
      action.payload.handleSuccess?.();
    });

    builder.addCase(updateSection, (state, action) => {
      const {
        section: newSection,
        pageReferenceId,
        sectionReferenceId,
      } = action.payload;

      const { pages } = state;

      if (pages) {
        const page = pages.find((e) => e.pageReferenceId === pageReferenceId);

        if (page) {
          const { sections } = page;

          if (sections) {
            const section = sections.find(
              (e) => e.sectionReferenceId === sectionReferenceId,
            );

            if (section) {
              const update: CustomerPortalSection = {
                blocks: section.blocks,
                parentPageId: section.parentPageId,
                sectionMeta: {
                  conditions: newSection?.sectionMeta?.conditions,
                },
                sectionName: newSection.sectionName,
                customerPortalSectionId: section.customerPortalSectionId,
                parentPortalId: section.parentPortalId,
                sectionReferenceId: section.sectionReferenceId,
                deleted: section.deleted,
              };
              const index = sections.indexOf(section);

              sections[index] = update;
            }
          }
        }
      }
      action.payload.handleSuccess?.();
    });

    builder.addCase(updateSectionName, (state, action) => {
      const { pageReferenceId, sectionReferenceId, sectionName } =
        action.payload;

      const { pages } = state;

      if (pages) {
        const page = pages.find((e) => e.pageReferenceId === pageReferenceId);

        if (page) {
          const { sections } = page;

          if (sections) {
            const section = sections.find(
              (e) => e.sectionReferenceId === sectionReferenceId,
            );

            if (section) {
              section.sectionName = sectionName;
            }
          }
        }
      }
      action.payload.handleSuccess?.();
    });

    builder.addCase(updateBlockInSection, (state, action) => {
      const { blockId, blockPayload, pageReferenceId, sectionReferenceId } =
        action.payload;

      const { pages } = state;

      if (pages) {
        const page = pages.find((e) => e.pageReferenceId === pageReferenceId);

        if (page) {
          const { sections } = page;

          if (sections) {
            const section = sections.find(
              (e) => e.sectionReferenceId === sectionReferenceId,
            );

            if (section) {
              const { blocks } = section;

              if (blocks) {
                const block = blocks.find((e) => e.blockId === blockId);

                if (block) {
                  const index = blocks.indexOf(block);
                  const update: CustomerPortalBlock = {
                    blockDefinition: blockPayload.blockDefinition,
                    widget: blockPayload.widget,
                    blockId,
                    blockName: block?.blockName,
                  };

                  blocks[index] = update;
                }
              }
            }
          }
        }
      }
      action.payload.handleSuccess?.();
    });

    builder.addCase(reorderPages, (state, action) => {
      const { destination, source } = action.payload;
      const { portalMeta } = state;

      if (destination !== undefined && source !== undefined) {
        if (portalMeta && portalMeta?.pagesOrder !== undefined) {
          portalMeta.pagesOrder = reorder(
            _.cloneDeep(portalMeta.pagesOrder),
            source,
            destination,
          );
        }
      }
    });

    builder.addCase(reorderSections, (state, action) => {
      const { destination, source, pageReferenceId } = action.payload;
      const { pages } = state;

      if (destination !== undefined && source !== undefined) {
        const page = pages?.find((e) => e?.pageReferenceId === pageReferenceId);

        if (page) {
          const { pageMeta, sections } = page;

          if (pageMeta && pageMeta?.sectionsOrder !== undefined) {
            pageMeta.sectionsOrder = reorder(
              _.cloneDeep(pageMeta?.sectionsOrder),
              source,
              destination,
            );
          } else {
            page.pageMeta = {
              ...(page?.pageMeta ?? {}),
              sectionsOrder: reorder(
                _.cloneDeep(sections?.map((e) => e?.sectionReferenceId) ?? []),
                source,
                destination,
              ),
            };
          }
        }
      }
    });

    builder.addCase(changeTheme, (state, action) => {
      state.customerPortalThemeId = action.payload.themeId;
    });

    builder.addCase(copyToPage, (state, action) => {
      const { pageReferenceId, sectionReferenceId } = action.payload;

      const { pages } = state;

      const page = pages.find((e) => e.pageReferenceId === pageReferenceId);
      const allSections = pages
        .flatMap((e) => e.sections ?? [])
        .filter((e) => !!e);

      if (page) {
        const section = allSections.find(
          (e) => e.sectionReferenceId === sectionReferenceId,
        );

        if (section) {
          const newSection: CustomerPortalSection = {
            blocks: section.blocks,
            customerPortalSectionId: getUndefinedNumber(),
            parentPageId: page.customerPortalPageId,
            parentPortalId: state.customerPortalId,
            sectionMeta: section.sectionMeta,
            sectionName: section.sectionName,
            sectionReferenceId: floatUidString(),
            deleted: false,
          };

          if (page.sections) {
            page.sections.push(newSection);
          } else {
            page.sections = [newSection];
          }
          if (page.pageMeta.sectionsOrder) {
            page.pageMeta.sectionsOrder.push(newSection.sectionReferenceId);
          } else {
            page.pageMeta.sectionsOrder = [newSection.sectionReferenceId];
          }
        }
      }
      action.payload.handleSuccess?.();
    });

    // find which page the section is in
    // remove if from that page
    // add it to the new page
    builder.addCase(moveToPage, (state, action) => {
      const { pageReferenceId, sectionReferenceId } = action.payload;

      const { pages } = state;

      const page = pages.find((e) => e.pageReferenceId === pageReferenceId);
      const allSections = pages
        .flatMap((e) => e.sections ?? [])
        .filter((e) => !!e);

      if (page) {
        const section = allSections.find(
          (e) => e.sectionReferenceId === sectionReferenceId,
        );

        if (section) {
          const newSection: CustomerPortalSection = {
            blocks: section.blocks,
            customerPortalSectionId: getUndefinedNumber(),
            parentPageId: page.customerPortalPageId,
            parentPortalId: state.customerPortalId,
            sectionMeta: section.sectionMeta,
            sectionName: section.sectionName,
            sectionReferenceId: floatUidString(),
            deleted: false,
          };

          if (page.sections) {
            page.sections.push(newSection);
          } else {
            page.sections = [newSection];
          }

          if (page.pageMeta.sectionsOrder) {
            page.pageMeta.sectionsOrder.push(newSection.sectionReferenceId);
          } else {
            page.pageMeta.sectionsOrder = [newSection.sectionReferenceId];
          }

          const oldPage = pages.find((e) =>
            e.sections?.find(
              (s) => s.sectionReferenceId === sectionReferenceId,
            ),
          );

          if (oldPage) {
            const oldSection = oldPage.sections?.find(
              (e) => e.sectionReferenceId === sectionReferenceId,
            );

            if (oldSection) {
              oldPage.sections = oldPage.sections?.filter(
                (e) => e.sectionReferenceId !== sectionReferenceId,
              );
              if (oldPage?.pageMeta?.sectionsOrder) {
                oldPage.pageMeta.sectionsOrder =
                  oldPage.pageMeta.sectionsOrder.filter(
                    (e) => e !== sectionReferenceId,
                  );
              }
            }
          }
        }
      }
      action.payload.handleSuccess?.();
    });

    // duplicateToPage
    builder.addCase(duplicateToPage, (state, action) => {
      const { pageReferenceId, sectionReferenceId } = action.payload;

      const { pages } = state;

      const page = pages.find((e) => e.pageReferenceId === pageReferenceId);

      if (page) {
        const sections = page?.sections ?? [];
        const section = sections.find(
          (e) => e.sectionReferenceId === sectionReferenceId,
        );

        if (section) {
          const newSection: CustomerPortalSection = {
            blocks: section.blocks,
            customerPortalSectionId: getUndefinedNumber(),
            parentPageId: page.customerPortalPageId,
            parentPortalId: state.customerPortalId,
            sectionMeta: section.sectionMeta,
            sectionName: section.sectionName,
            sectionReferenceId: floatUidString(),
            deleted: false,
          };

          if (page.sections) {
            page.sections.push(newSection);
          } else {
            page.sections = [newSection];
          }
          if (page.pageMeta.sectionsOrder) {
            const indexOfSection =
              page.pageMeta.sectionsOrder.indexOf(sectionReferenceId);

            page.pageMeta.sectionsOrder = [
              ...page.pageMeta.sectionsOrder.slice(0, indexOfSection + 1),
              newSection.sectionReferenceId,
              ...page.pageMeta.sectionsOrder.slice(indexOfSection + 1),
            ];
          } else {
            page.pageMeta.sectionsOrder = [newSection.sectionReferenceId];
          }
          action.payload.handleSuccess?.(newSection?.sectionReferenceId);
        }
      }
    });
  },
);
